import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ChevronRight } from "lucide-react";

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const scrollRef = useRef(null);

  const faqs = [
    { 
      question: "What is included in your branding services?", 
      answer: "Our comprehensive branding services include logo design, complete brand guidelines, custom color palettes, typography systems, brand strategy development, and visual identity design to create a cohesive and memorable brand presence.",
      category: "Branding"
    },
    { 
      question: "How long does it take to complete a branding project?", 
      answer: "Typically between 4–6 weeks, depending on feedback rounds and project scope. We follow an agile approach with weekly milestones to ensure timely delivery while maintaining quality standards.",
      category: "Timeline"
    },
    { 
      question: "Do you offer mobile-friendly designs?", 
      answer: "Absolutely. All our websites are fully responsive and optimized for phones, tablets, and desktops. We use mobile-first design principles to ensure optimal performance across all devices and screen sizes.",
      category: "Design"
    },
    { 
      question: "Can you redesign an existing website?", 
      answer: "Yes, we specialize in transforming outdated websites with modern design, improved performance, and enhanced user experience. We analyze your current site and provide strategic recommendations for maximum impact.",
      category: "Development"
    },
    { 
      question: "Do you provide custom development solutions?", 
      answer: "Yes, we build fully custom web and mobile applications using modern frameworks like React, Next.js, and React Native. Our solutions are scalable, secure, and tailored to your specific business needs.",
      category: "Development"
    },
    { 
      question: "Will I be able to update the website on my own?", 
      answer: "Definitely. We build user-friendly CMS systems with intuitive interfaces, allowing you to easily edit content, add pages, and manage your site without any coding knowledge required.",
      category: "Support"
    },
    { 
      question: "How do you approach digital marketing campaigns?", 
      answer: "We design data-driven campaigns across SEO, email marketing, social media, and PPC platforms. Our strategy includes audience research, competitor analysis, and continuous optimization to drive measurable growth for your brand.",
      category: "Marketing"
    },
  ];

  const categories = [...new Set(faqs.map(faq => faq.category))];

  // Lock/unlock page scroll when FAQ area is hovered
  useEffect(() => {
    const scrollable = scrollRef.current;

    const handleMouseEnter = () => {
      document.body.style.overflow = "hidden";
    };
    const handleMouseLeave = () => {
      document.body.style.overflow = "auto";
    };

    if (scrollable) {
      scrollable.addEventListener("mouseenter", handleMouseEnter);
      scrollable.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (scrollable) {
        scrollable.removeEventListener("mouseenter", handleMouseEnter);
        scrollable.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-gray-50 to-blue-50 py-24 px-6 md:px-16 lg:px-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
      <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start relative z-10">
        {/* LEFT - Static Content */}
        <motion.div 
          className="sticky top-32 self-start"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center justify-start mb-4">
            <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
            <p className="text-blue-600 font-semibold text-lg tracking-wider">
              FREQUENTLY ASKED QUESTIONS
            </p>
            <div className="w-2 h-2 bg-blue-600 rounded-full ml-3"></div>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Clear Solutions
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
              To Your Questions
            </span>
          </h2>
          
          <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-8"></div>

          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            Find answers to common questions about our services, process, and how we can help bring your digital vision to life.
          </p>

          {/* Category Tags */}
          <div className="flex flex-wrap gap-3 mb-8">
            {categories.map((category, index) => (
              <span
                key={category}
                className="bg-white/80 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-full text-sm font-medium shadow-sm border border-gray-200/50"
              >
                {category}
              </span>
            ))}
          </div>

          {/* Image Card */}
          <motion.div 
            className="rounded-3xl overflow-hidden shadow-2xl border border-white/50 bg-white/80 backdrop-blur-sm"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="Team collaboration and support"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Need More Help?</h3>
              <p className="text-gray-600 mb-4">Can't find what you're looking for? Our support team is here to help.</p>
              <button className="flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-300">
                Contact Support <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </motion.div>
        </motion.div>

        {/* RIGHT - Scrollable FAQ */}
        <motion.div
          ref={scrollRef}
          className="max-h-[75vh] overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {faqs.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="mb-4 group"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <motion.div
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50 hover:shadow-xl transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  borderColor: "rgba(59, 130, 246, 0.3)"
                }}
                animate={{
                  borderColor: openIndex === index ? "rgba(59, 130, 246, 0.5)" : "rgba(255, 255, 255, 0.5)"
                }}
              >
                <button
                  className="w-full flex justify-between items-center text-left focus:outline-none"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                >
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full font-medium mr-3">
                        {item.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {item.question}
                    </h3>
                  </div>
                  <motion.div
                    className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    animate={{ 
                      rotate: openIndex === index ? 180 : 0,
                      backgroundColor: openIndex === index ? "#1e40af" : "#2563eb"
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {openIndex === index ? (
                      <Minus className="w-4 h-4" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                  </motion.div>
                </button>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 mt-4 border-t border-gray-200/50">
                        <p className="text-gray-700 leading-relaxed text-lg">
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Background Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl opacity-0 group-hover:opacity-10 blur-xl -z-10 transition-all duration-500 transform group-hover:scale-105"></div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* CTA Section */}
      <motion.div 
        className="text-center mt-16 relative z-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-12 text-white shadow-2xl max-w-4xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Still Have Questions?
          </h3>
          <p className="text-blue-100 mb-8 text-lg max-w-2xl mx-auto">
            Our team is here to help you get all the answers you need to move forward with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Our Team
            </motion.button>
            <motion.button
              className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white hover:text-blue-600 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule a Call
            </motion.button>
          </div>
        </div>
      </motion.div>

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
}